import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, ArrowClockwise, Shield, Trash } from "@phosphor-icons/react";

const REWARD_TYPES = ["credits", "cash", "xp"];

const EVENT_META = {
  register: { label: "New signup", hint: "Paid immediately when a referred user creates an account" },
  verify_email: { label: "Email verified", hint: "Paid when referral confirms their email address" },
  earn_surf: { label: "Referral surfs", hint: "% of the credits the referral earns on each surf" },
  purchase_credits: { label: "Purchases credits", hint: "% of the $ amount they spend, paid in cash" },
  purchase_membership: { label: "Purchases membership", hint: "% of the plan price, paid in cash" },
  daily_active: { label: "Daily active", hint: "Recurring bonus while referral stays active (off by default)" },
};

export default function AdminReferrals() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [fraud, setFraud] = useState([]);

  const load = async () => {
    try {
      const [c, e, f] = await Promise.all([
        api.get("/admin/referrals/config"),
        api.get("/admin/referrals/events?limit=100"),
        api.get("/admin/referrals/fraud"),
      ]);
      setCfg(c.data);
      setEvents(e.data);
      setFraud(f.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/referrals/config", cfg);
      setCfg(data);
      toast.success("Referral config saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const resizeArrays = (levels) => {
    setCfg((c) => {
      const next = { ...c, max_levels: levels, rules: { ...c.rules } };
      for (const ev of Object.keys(next.rules)) {
        for (const rt of REWARD_TYPES) {
          const arr = [...(next.rules[ev][rt] || [])];
          while (arr.length < levels) arr.push(0);
          arr.length = levels;
          next.rules[ev][rt] = arr;
        }
      }
      return next;
    });
  };

  const setCell = (event, rt, i, val) => {
    setCfg((c) => {
      const rules = { ...c.rules, [event]: { ...c.rules[event] } };
      const arr = [...(rules[event][rt] || [])];
      arr[i] = Number(val);
      rules[event][rt] = arr;
      return { ...c, rules };
    });
  };

  const setEventFlag = (event, key, val) => {
    setCfg((c) => ({ ...c, rules: { ...c.rules, [event]: { ...c.rules[event], [key]: val } } }));
  };

  const reverseEvent = async (id) => {
    if (!window.confirm("Reverse this commission?")) return;
    try {
      await api.post(`/admin/referrals/events/${id}/reverse`);
      toast.success("Commission reversed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!cfg) return <AdminLayout><div className="text-slate-500">Loading…</div></AdminLayout>;

  const maxLevels = cfg.max_levels;

  return (
    <AdminLayout>
      <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white mb-5" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Referral Program</h1>

      {/* Global controls */}
      <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 mb-5 grid md:grid-cols-4 gap-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Max levels (1–10)</label>
          <input
            data-testid="cfg-max-levels"
            type="number" min={1} max={10}
            value={cfg.max_levels}
            onChange={(e) => resizeArrays(Math.max(1, Math.min(10, Number(e.target.value))))}
            className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Active window (days)</label>
          <input
            data-testid="cfg-active-window"
            type="number" min={1}
            value={cfg.active_days_window}
            onChange={(e) => setCfg({ ...cfg, active_days_window: Number(e.target.value) })}
            className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
          />
        </div>
        <label className="flex items-center gap-3 rounded-xl bg-slate-50 dark:bg-slate-800 p-3 self-end">
          <input data-testid="cfg-require-active" type="checkbox" checked={!!cfg.require_active_for_commission}
                 onChange={(e) => setCfg({ ...cfg, require_active_for_commission: e.target.checked })} className="h-5 w-5" />
          <span className="text-sm font-semibold">Require active for commission</span>
        </label>
        <label className="flex items-center gap-3 rounded-xl bg-slate-50 dark:bg-slate-800 p-3 self-end">
          <input data-testid="cfg-same-ip-block" type="checkbox" checked={!!cfg.same_ip_block}
                 onChange={(e) => setCfg({ ...cfg, same_ip_block: e.target.checked })} className="h-5 w-5" />
          <span className="text-sm font-semibold">Flag same-IP signups</span>
        </label>
      </section>

      {/* Per-event grids */}
      <div className="space-y-5">
        {Object.entries(cfg.rules).map(([event, rule]) => (
          <section key={event} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
                    {EVENT_META[event]?.label || event}
                  </h2>
                  <code className="text-[11px] bg-slate-100 dark:bg-slate-800 rounded px-1.5 py-0.5 text-slate-600 dark:text-slate-300">{event}</code>
                </div>
                {EVENT_META[event]?.hint && <div className="text-xs text-slate-500 mt-0.5">{EVENT_META[event].hint}</div>}
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Mode</label>
                <select
                  data-testid={`cfg-mode-${event}`}
                  value={rule.mode || "percent"}
                  onChange={(e) => setEventFlag(event, "mode", e.target.value)}
                  className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2 py-1 text-xs"
                >
                  <option value="percent">% of base</option>
                  <option value="fixed">fixed amount</option>
                </select>
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input
                    data-testid={`cfg-enabled-${event}`}
                    type="checkbox"
                    checked={!!rule.enabled}
                    onChange={(e) => setEventFlag(event, "enabled", e.target.checked)}
                    className="h-4 w-4"
                  />
                  Enabled
                </label>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase font-bold tracking-widest text-slate-500">
                    <th className="p-2 text-left">Reward type</th>
                    {Array.from({ length: maxLevels }, (_, i) => (
                      <th key={i} className="p-2 text-center">L{i + 1}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {REWARD_TYPES.map((rt) => (
                    <tr key={rt} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="p-2 font-bold text-slate-700 dark:text-slate-200 capitalize">{rt}</td>
                      {Array.from({ length: maxLevels }, (_, i) => (
                        <td key={i} className="p-1">
                          <input
                            data-testid={`cfg-${event}-${rt}-L${i + 1}`}
                            type="number" step="0.01" min="0"
                            value={rule[rt]?.[i] ?? 0}
                            onChange={(e) => setCell(event, rt, i, e.target.value)}
                            className="w-full text-center rounded-md bg-slate-100 dark:bg-slate-800 px-1 py-1 text-slate-900 dark:text-slate-100 border border-transparent focus:border-blue-500 focus:outline-none"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>

      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <button
          data-testid="cfg-save-btn"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-black px-6 py-3 transition-colors duration-200"
        >
          <FloppyDisk size={18} weight="bold" /> {saving ? "Saving…" : "Save configuration"}
        </button>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-bold px-4 py-2 transition-colors duration-200"
        >
          <ArrowClockwise size={14} weight="bold" /> Reload
        </button>
      </div>

      {/* Fraud flags */}
      <section className="mt-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Shield size={18} weight="bold" className="text-red-500" />
          <h2 className="text-lg font-black text-slate-900 dark:text-white">Fraud flags <span className="text-slate-400 text-sm font-normal">({fraud.length} open)</span></h2>
        </div>
        {fraud.length === 0 ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">No suspicious signups detected.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase font-bold tracking-widest text-slate-500">
                <tr>
                  <th className="p-2 text-left">User</th>
                  <th className="p-2 text-left">Sponsor</th>
                  <th className="p-2 text-left">Reasons</th>
                  <th className="p-2 text-left">Signup IP</th>
                  <th className="p-2 text-left">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {fraud.map((f, i) => (
                  <tr key={i}>
                    <td className="p-2 font-bold">@{f.user?.username || "?"}</td>
                    <td className="p-2">@{f.sponsor?.username || "—"}</td>
                    <td className="p-2 text-red-500 font-bold">{(f.reasons || []).join(", ")}</td>
                    <td className="p-2 font-mono text-xs">{f.signup_ip || "—"}</td>
                    <td className="p-2 text-xs text-slate-500">{new Date(f.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Recent commissions with reverse */}
      <section className="mt-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
        <h2 className="text-lg font-black text-slate-900 dark:text-white mb-3">Recent commissions (100)</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase font-bold tracking-widest text-slate-500">
              <tr>
                <th className="p-2 text-left">When</th>
                <th className="p-2 text-left">Upline</th>
                <th className="p-2 text-left">Source</th>
                <th className="p-2 text-left">Event</th>
                <th className="p-2 text-center">L</th>
                <th className="p-2 text-right">Reward</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="admin-ref-events-table">
              {events.map((e) => (
                <tr key={e.id}>
                  <td className="p-2 text-slate-500">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="p-2 font-mono text-[11px]">{e.upline_user_id.slice(0, 8)}</td>
                  <td className="p-2 font-mono text-[11px]">{e.source_user_id?.slice(0, 8)}</td>
                  <td className="p-2">{e.event}</td>
                  <td className="p-2 text-center font-bold">{e.level}</td>
                  <td className="p-2 text-right font-black">
                    {e.reward_type === "cash" ? `$${Number(e.amount).toFixed(4)}` : `+${Number(e.amount).toFixed(2)} ${e.reward_type}`}
                  </td>
                  <td className="p-2">
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full ${
                      e.status === "paid" ? "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300" :
                      e.status === "reversed" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"
                    }`}>{e.status}</span>
                  </td>
                  <td className="p-2">
                    {e.status === "paid" && (
                      <button
                        onClick={() => reverseEvent(e.id)}
                        data-testid={`reverse-${e.id}`}
                        className="text-red-500 hover:text-red-600 text-xs font-bold hover:underline inline-flex items-center gap-1"
                      >
                        <Trash size={12} weight="bold" /> reverse
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </AdminLayout>
  );
}
